<template>
  <v-dialog v-model="isOpen" max-width="1100" scrollable>
    <div class="provenance-dialog">
      <header class="provenance-header">
        <div>
          <div class="provenance-title">AI Detection Trace</div>
          <div class="provenance-subtitle">
            Evidence behind this marker — Duallo admin only
          </div>
        </div>
        <button class="icon-btn" @click="isOpen = false">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </header>

      <div v-if="loading" class="provenance-loading">
        <v-progress-circular indeterminate color="#66c7b4" size="36" />
      </div>

      <div v-else-if="error" class="provenance-empty">
        <v-icon size="32" color="#c76666">mdi-alert-circle-outline</v-icon>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="!canonical" class="provenance-empty">
        <v-icon size="32">mdi-database-off-outline</v-icon>
        <p>No trace was recorded for this marker.</p>
        <span class="hint">
          Traces are written by the inference service; markers created before that
          was enabled, or created manually, have none.
        </span>
      </div>

      <div v-else class="provenance-body">
        <!-- Observation selector: the detection that became the marker, plus any
             folded in as duplicates. Comparing them side by side is what reveals
             whether a "duplicate" is really one anomaly placed inconsistently. -->
        <div v-if="observations.length > 1" class="observation-tabs">
          <button v-for="(obs, index) in observations" :key="obs.id" class="observation-tab"
            :class="{ active: obs.id === selected?.id }" @click="selected = obs">
            <span class="tab-label">{{ index === 0 ? 'Marker' : `Duplicate ${index}` }}</span>
            <span class="tab-sub">{{ obs.sourceImageFileName }}</span>
          </button>
        </div>

        <div class="provenance-layout">
          <section class="image-panel">
            <div v-if="selectedImageUrl" ref="imageWrapper" class="image-wrapper"
              :class="{ zoomed: zoom > 1, panning: isPanning }" @wheel.prevent="onWheel"
              @mousedown.prevent="onPanStart" @dblclick="resetZoom">
              <!-- Image + overlays share one transformed layer, so the bbox and
                   crosshairs stay glued to their pixels at any zoom level. -->
              <div class="zoom-layer" :style="zoomStyle">
                <img :src="selectedImageUrl" class="frame-image" alt="Source frame" @load="onImageLoad" />

                <!-- Overlay is positioned in percentages of the natural image size,
                     so it stays aligned regardless of how the image is scaled. -->
                <div v-if="bboxStyle" class="bbox-overlay" :style="bboxStyle">
                  <span class="bbox-tag">
                    {{ selected.label }} · {{ (selected.confidence * 100).toFixed(0) }}%
                  </span>
                </div>

                <!-- Round-trip check: the marker's 3D position projected back through
                     the camera helper. It should land inside the box above. -->
                <template v-if="showProjection && projection">
                  <div v-if="projectedStyle" class="projection-marker" :style="projectedStyle">
                    <span class="projection-tag">projected</span>
                  </div>

                  <div v-if="projectedFlippedStyle" class="projection-marker flipped" :style="projectedFlippedStyle">
                    <span class="projection-tag flipped">Y-flipped</span>
                  </div>
                </template>
              </div>

              <div class="zoom-controls" @mousedown.stop @dblclick.stop>
                <button class="zoom-btn" title="Zoom out" :disabled="zoom <= 1" @click="zoomBy(1 / 1.4)">
                  <v-icon size="16">mdi-minus</v-icon>
                </button>
                <span class="zoom-level">{{ Math.round(zoom * 100) }}%</span>
                <button class="zoom-btn" title="Zoom in" :disabled="zoom >= maxZoom" @click="zoomBy(1.4)">
                  <v-icon size="16">mdi-plus</v-icon>
                </button>
                <button class="zoom-btn" title="Zoom to detection" @click="zoomToBbox">
                  <v-icon size="16">mdi-image-filter-center-focus</v-icon>
                </button>
                <button class="zoom-btn" title="Reset (double-click image)" :disabled="zoom <= 1" @click="resetZoom">
                  <v-icon size="16">mdi-fit-to-screen-outline</v-icon>
                </button>
              </div>
            </div>

            <div v-else class="image-missing">
              <v-icon size="32">mdi-image-off-outline</v-icon>
              <p>Source frame unavailable</p>
              <span class="hint">{{ selected.sourceImageFileName || 'No frame recorded' }}</span>
            </div>

            <div class="image-actions">
              <button class="btn ghost-btn" :disabled="!cameraHelper" @click="showProjection = !showProjection">
                <v-icon size="16" start>mdi-crosshairs-gps</v-icon>
                {{ showProjection ? 'Hide projection' : 'Show projection' }}
              </button>
              <span v-if="!cameraHelper" class="hint">No camera helper for this frame</span>
            </div>

            <p class="image-caption">
              The box shows exactly what the model detected. If it sits on the right
              defect, the detection was good and any error is in placement.
            </p>

            <div v-if="showProjection && projection" class="projection-readout">
              <div class="detail-row">
                <span class="detail-label">Projected pixel</span>
                <span class="detail-value mono">
                  {{ projection.x.toFixed(0) }}, {{ projection.y.toFixed(0) }}
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Inside bounding box</span>
                <span class="detail-value" :class="projectionInsideBbox ? 'ok' : 'bad'">
                  {{ projectionInsideBbox ? 'Yes' : 'No' }}
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Y-flipped inside box</span>
                <span class="detail-value" :class="flippedInsideBbox ? 'ok' : 'bad'">
                  {{ flippedInsideBbox ? 'Yes' : 'No' }}
                </span>
              </div>
              <p v-if="projection.isBehindCamera" class="detail-warning">
                <v-icon size="14">mdi-alert-outline</v-icon>
                The marker sits behind this camera — it could not have been seen from here.
              </p>
              <p v-else-if="!projectionInsideBbox && flippedInsideBbox" class="detail-warning">
                <v-icon size="14">mdi-alert-outline</v-icon>
                Only the Y-flipped projection lands in the box — the vertical pixel
                origin disagrees between the detection and the visibility data.
              </p>
              <p v-else-if="!projectionInsideBbox" class="detail-warning">
                <v-icon size="14">mdi-alert-outline</v-icon>
                The marker does not project back into its own bounding box, so this
                position did not come from this detection's line of sight.
              </p>
            </div>
          </section>

          <aside class="detail-panel">
            <div class="detail-group">
              <div class="detail-group-title">Detection</div>
              <div class="detail-row">
                <span class="detail-label">Class</span>
                <span class="detail-value">{{ selected.label }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Confidence</span>
                <span class="detail-value">{{ (selected.confidence * 100).toFixed(1) }}%</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Frame</span>
                <span class="detail-value mono">{{ selected.sourceImageFileName || '—' }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Bounding box</span>
                <span class="detail-value mono">
                  {{ selected.bboxWidth.toFixed(0) }} × {{ selected.bboxHeight.toFixed(0) }} px
                  @ ({{ selected.bboxX.toFixed(0) }}, {{ selected.bboxY.toFixed(0) }})
                </span>
              </div>
            </div>

            <div class="detail-group">
              <div class="detail-group-title">Placement</div>
              <div class="detail-row">
                <span class="detail-label">Position</span>
                <span class="detail-value mono">{{ formatVector(selected.position) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Size</span>
                <span class="detail-value mono">{{ formatVector(selected.size) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Depth from camera</span>
                <span class="detail-value mono">{{ selected.depth.toFixed(3) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Points used</span>
                <span class="detail-value mono">
                  {{ selected.inlierCount }} / {{ selected.candidateCount }}
                </span>
              </div>
              <p v-if="placementWarning" class="detail-warning">
                <v-icon size="14">mdi-alert-outline</v-icon>
                {{ placementWarning }}
              </p>
            </div>

            <div v-if="duplicates.length" class="detail-group">
              <div class="detail-group-title">Duplicates merged</div>
              <div class="detail-row">
                <span class="detail-label">Count</span>
                <span class="detail-value">{{ duplicates.length }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Max spread</span>
                <span class="detail-value mono">{{ duplicateSpread.toFixed(3) }}</span>
              </div>
              <p v-if="duplicateSpread > 1" class="detail-warning">
                <v-icon size="14">mdi-alert-outline</v-icon>
                Merged detections are far apart — the same defect was likely placed
                inconsistently rather than genuinely detected twice.
              </p>
            </div>

            <div class="detail-group">
              <div class="detail-group-title">Reference</div>
              <div class="detail-row">
                <span class="detail-label">Trace ID</span>
                <span class="detail-value mono small">{{ selected.id }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Detection ID</span>
                <span class="detail-value mono small">{{ selected.detectionId || '—' }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">PLY point</span>
                <span class="detail-value mono">{{ selected.pointIndex }}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, defineProps, defineEmits } from 'vue';
import MarkerProvenanceService from '@/services/MarkerProvenanceService';

const props = defineProps({
  modelValue: Boolean,
  marker: Object,
  scanId: String
});

const emit = defineEmits(['update:modelValue']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const loading = ref(false);
const error = ref(null);
const canonical = ref(null);
const duplicates = ref([]);
const selected = ref(null);
const frameUrlsByStem = ref({});
const cameraHelpersByStem = ref({});
const showProjection = ref(false);
const naturalSize = ref({ width: 0, height: 0 });

// --- Zoom / pan -------------------------------------------------------------
// transform: translate(pan) scale(zoom) with origin 0 0, so a layer point p
// renders at pan + zoom * p. The wrapper keeps its layout size (transforms
// don't affect layout) and clips the overflow, acting as the viewport.
const imageWrapper = ref(null);
const maxZoom = 10;
const zoom = ref(1);
const panX = ref(0);
const panY = ref(0);
const isPanning = ref(false);
let panStart = null;

const zoomStyle = computed(() => ({
  transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoom.value})`,
  transformOrigin: '0 0'
}));

/** Keeps the layer covering the viewport — no blank gutters while panning. */
const clampPan = () => {
  const el = imageWrapper.value;
  if (!el) return;
  panX.value = Math.min(0, Math.max(el.clientWidth - zoom.value * el.clientWidth, panX.value));
  panY.value = Math.min(0, Math.max(el.clientHeight - zoom.value * el.clientHeight, panY.value));
};

/** Zooms so the viewport point (cx, cy) stays put — the cursor-anchored zoom. */
const applyZoom = (factor, cx, cy) => {
  const next = Math.min(maxZoom, Math.max(1, zoom.value * factor));
  if (next === zoom.value) return;

  panX.value = cx - ((cx - panX.value) * next) / zoom.value;
  panY.value = cy - ((cy - panY.value) * next) / zoom.value;
  zoom.value = next;

  if (next === 1) { panX.value = 0; panY.value = 0; }
  clampPan();
};

const onWheel = (event) => {
  const rect = imageWrapper.value?.getBoundingClientRect();
  if (!rect) return;
  applyZoom(event.deltaY < 0 ? 1.25 : 0.8, event.clientX - rect.left, event.clientY - rect.top);
};

const zoomBy = (factor) => {
  const el = imageWrapper.value;
  if (!el) return;
  applyZoom(factor, el.clientWidth / 2, el.clientHeight / 2);
};

/** Jumps straight to the detection: bbox centred, filling ~40% of the view. */
const zoomToBbox = () => {
  const el = imageWrapper.value;
  if (!el || !selected.value || !naturalSize.value.width || !naturalSize.value.height) return;

  const { left, top, width, height } = selected.value.bboxRect;
  const fracX = (left + width / 2) / naturalSize.value.width;
  const fracY = (top + height / 2) / naturalSize.value.height;
  const fracW = width / naturalSize.value.width;
  const fracH = height / naturalSize.value.height;

  zoom.value = Math.min(maxZoom, Math.max(1, 0.4 / Math.max(fracW, fracH, 0.01)));
  panX.value = el.clientWidth / 2 - zoom.value * fracX * el.clientWidth;
  panY.value = el.clientHeight / 2 - zoom.value * fracY * el.clientHeight;
  clampPan();
};

const resetZoom = () => {
  zoom.value = 1;
  panX.value = 0;
  panY.value = 0;
};

const onPanMove = (event) => {
  if (!panStart) return;
  panX.value = panStart.panX + (event.clientX - panStart.x);
  panY.value = panStart.panY + (event.clientY - panStart.y);
  clampPan();
};

const onPanEnd = () => {
  panStart = null;
  isPanning.value = false;
  window.removeEventListener('mousemove', onPanMove);
  window.removeEventListener('mouseup', onPanEnd);
};

const onPanStart = (event) => {
  if (zoom.value <= 1) return;
  panStart = { x: event.clientX, y: event.clientY, panX: panX.value, panY: panY.value };
  isPanning.value = true;
  window.addEventListener('mousemove', onPanMove);
  window.addEventListener('mouseup', onPanEnd);
};

onBeforeUnmount(onPanEnd);
// ----------------------------------------------------------------------------

const cameraHelper = computed(() => {
  if (!selected.value) return null;
  const stem = MarkerProvenanceService.stemOf(selected.value.sourceImageFileName);
  return cameraHelpersByStem.value[stem] ?? null;
});

/** The marker's own 3D position projected back into this frame. */
const projection = computed(() => {
  if (!cameraHelper.value || !selected.value) return null;
  return MarkerProvenanceService.projectToImage(cameraHelper.value, selected.value.position);
});

const isInsideBbox = (x, y) => {
  if (!selected.value || x == null || y == null) return false;
  const { left, top, width, height } = selected.value.bboxRect;
  return x >= left && x <= left + width && y >= top && y <= top + height;
};

const projectionInsideBbox = computed(() =>
  projection.value ? isInsideBbox(projection.value.x, projection.value.y) : false
);

const flippedInsideBbox = computed(() =>
  projection.value ? isInsideBbox(projection.value.x, projection.value.flippedY) : false
);

/** Percentage position for a projected pixel, or null when off-image. */
const projectedPointStyle = (x, y) => {
  if (!naturalSize.value.width || !naturalSize.value.height || x == null || y == null) return null;
  return {
    left: `${(x / naturalSize.value.width) * 100}%`,
    top: `${(y / naturalSize.value.height) * 100}%`
  };
};

const projectedStyle = computed(() =>
  projection.value ? projectedPointStyle(projection.value.x, projection.value.y) : null
);

const projectedFlippedStyle = computed(() =>
  projection.value ? projectedPointStyle(projection.value.x, projection.value.flippedY) : null
);

const observations = computed(() =>
  canonical.value ? [canonical.value, ...duplicates.value] : []
);

const selectedImageUrl = computed(() => {
  if (!selected.value) return null;
  const stem = MarkerProvenanceService.stemOf(selected.value.sourceImageFileName);
  return frameUrlsByStem.value[stem] ?? null;
});

/**
 * Percentage-based box so the overlay tracks the image at any rendered size.
 * Needs the natural dimensions, which only exist once the image has loaded.
 */
const bboxStyle = computed(() => {
  if (!selected.value || !naturalSize.value.width || !naturalSize.value.height) return null;

  const { left, top, width, height } = selected.value.bboxRect;
  const w = naturalSize.value.width;
  const h = naturalSize.value.height;

  return {
    left: `${(left / w) * 100}%`,
    top: `${(top / h) * 100}%`,
    width: `${(width / w) * 100}%`,
    height: `${(height / h) * 100}%`
  };
});

const placementWarning = computed(() => {
  const obs = selected.value;
  if (!obs) return null;

  if (obs.candidateCount === 0) return 'No cloud points fell inside the box.';

  // A small surviving fraction means the box spanned several surfaces and the
  // depth choice was close to a coin flip.
  const ratio = obs.inlierCount / obs.candidateCount;
  if (obs.candidateCount >= 5 && ratio < 0.35) {
    return 'The box covered several surfaces — this position is low confidence.';
  }
  if (obs.candidateCount < 5) {
    return 'Very few cloud points supported this position.';
  }
  return null;
});

const duplicateSpread = computed(() => {
  if (!canonical.value || !duplicates.value.length) return 0;

  const origin = canonical.value.position;
  return duplicates.value.reduce((max, dupe) => {
    const dx = (dupe.position?.x ?? 0) - (origin?.x ?? 0);
    const dy = (dupe.position?.y ?? 0) - (origin?.y ?? 0);
    const dz = (dupe.position?.z ?? 0) - (origin?.z ?? 0);
    return Math.max(max, Math.sqrt(dx * dx + dy * dy + dz * dz));
  }, 0);
});

const formatVector = (vector) => {
  if (!vector) return '—';
  return `${(vector.x ?? 0).toFixed(3)}, ${(vector.y ?? 0).toFixed(3)}, ${(vector.z ?? 0).toFixed(3)}`;
};

const onImageLoad = (event) => {
  naturalSize.value = {
    width: event.target.naturalWidth,
    height: event.target.naturalHeight
  };
};

const load = async () => {
  loading.value = true;
  error.value = null;
  canonical.value = null;
  duplicates.value = [];
  selected.value = null;
  frameUrlsByStem.value = {};
  cameraHelpersByStem.value = {};
  showProjection.value = false;
  resetZoom();

  try {
    const result = await MarkerProvenanceService.getForMarker(props.scanId, props.marker);

    if (!result) return;

    canonical.value = result.canonical;
    duplicates.value = result.duplicates;
    selected.value = result.canonical;

    const observationList = [result.canonical, ...result.duplicates];

    const [urls, helpers] = await Promise.all([
      MarkerProvenanceService.getFrameUrlsByStem(props.scanId, observationList),
      MarkerProvenanceService.getCameraHelpersByStem(props.scanId)
    ]);

    frameUrlsByStem.value = urls;
    cameraHelpersByStem.value = helpers;
  } catch (ex) {
    error.value = 'Could not load the detection trace.';
    console.error('[MarkerProvenance] load failed', ex);
  } finally {
    loading.value = false;
  }
};

// Reset natural size and zoom when switching observation so the overlay never
// renders against the previous frame's dimensions or viewport.
watch(selected, () => {
  naturalSize.value = { width: 0, height: 0 };
  resetZoom();
});

watch(() => props.modelValue, (open) => {
  if (open && props.marker && props.scanId) load();
});
</script>

<style scoped>
.provenance-dialog {
  background: #1e1e1e;
  color: #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
}

.provenance-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #2f2f2f;
}

.provenance-title {
  font-size: 1.05rem;
  font-weight: 600;
}

.provenance-subtitle {
  font-size: 0.78rem;
  color: #9a9a9a;
  margin-top: 2px;
}

.icon-btn {
  background: transparent;
  border: none;
  color: #9a9a9a;
  cursor: pointer;
  padding: 4px;
}

.icon-btn:hover {
  color: #e8e8e8;
}

.provenance-loading,
.provenance-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 54px 22px;
  color: #9a9a9a;
  text-align: center;
}

.provenance-empty .hint {
  font-size: 0.75rem;
  max-width: 380px;
  color: #7a7a7a;
}

.observation-tabs {
  display: flex;
  gap: 8px;
  padding: 14px 22px 0;
  flex-wrap: wrap;
}

.observation-tab {
  background: #262626;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  text-align: left;
  color: #cfcfcf;
}

.observation-tab.active {
  border-color: var(--duallo-accent-muted);
  background: #23302e;
}

.tab-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
}

.tab-sub {
  display: block;
  font-size: 0.68rem;
  color: #8a8a8a;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.provenance-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(260px, 1fr);
  gap: 20px;
  padding: 18px 22px 24px;
}

@media (max-width: 900px) {
  .provenance-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

.image-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
  background: #111;
  border-radius: 8px;
  overflow: hidden;
  line-height: 0;
}

.image-wrapper.zoomed {
  cursor: grab;
}

.image-wrapper.panning {
  cursor: grabbing;
}

.zoom-layer {
  position: relative;
  width: 100%;
  line-height: 0;
  will-change: transform;
}

.zoom-controls {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(20, 20, 20, 0.85);
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  padding: 3px 6px;
  line-height: normal;
}

.zoom-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #cfcfcf;
  cursor: pointer;
}

.zoom-btn:hover:not(:disabled) {
  background: #333;
  color: #fff;
}

.zoom-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.zoom-level {
  min-width: 42px;
  text-align: center;
  font-size: 0.72rem;
  color: #b0b0b0;
  font-variant-numeric: tabular-nums;
}

.frame-image {
  width: 100%;
  height: auto;
  display: block;
}

.bbox-overlay {
  position: absolute;
  border: 2px solid var(--duallo-accent-muted);
  background: rgba(102, 199, 180, 0.22);
  box-sizing: border-box;
  pointer-events: none;
}

.bbox-tag {
  position: absolute;
  top: -22px;
  left: -2px;
  background: var(--duallo-accent-muted);
  color: #10201d;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 3px;
  white-space: nowrap;
  line-height: 1.6;
}

/* Crosshair at a projected pixel. Centred on its own position so the
   intersection sits exactly on the coordinate. */
.projection-marker {
  position: absolute;
  width: 18px;
  height: 18px;
  margin: -9px 0 0 -9px;
  pointer-events: none;
}

.projection-marker::before,
.projection-marker::after {
  content: "";
  position: absolute;
  background: #f2b134;
}

.projection-marker::before {
  left: 50%;
  top: 0;
  width: 2px;
  height: 100%;
  margin-left: -1px;
}

.projection-marker::after {
  top: 50%;
  left: 0;
  height: 2px;
  width: 100%;
  margin-top: -1px;
}

.projection-marker.flipped::before,
.projection-marker.flipped::after {
  background: #d264d2;
}

.projection-tag {
  position: absolute;
  left: 22px;
  top: -6px;
  background: #f2b134;
  color: #201a10;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  white-space: nowrap;
  line-height: 1.6;
}

.projection-tag.flipped {
  background: #d264d2;
  color: #2a112a;
}

.image-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #262626;
  border: 1px solid #3a3a3a;
  color: #cfcfcf;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 0.78rem;
  cursor: pointer;
}

.ghost-btn:hover:not(:disabled) {
  border-color: var(--duallo-accent-muted);
  color: #e8e8e8;
}

.ghost-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.image-actions .hint {
  font-size: 0.72rem;
  color: #7a7a7a;
}

.projection-readout {
  margin-top: 10px;
  background: #242424;
  border-radius: 8px;
  padding: 10px 14px;
}

.detail-value.ok {
  color: var(--duallo-accent-muted);
}

.detail-value.bad {
  color: #d8b45f;
}

.image-missing {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  background: #161616;
  border-radius: 8px;
  color: #8a8a8a;
}

.image-missing .hint {
  font-size: 0.72rem;
  color: #6f6f6f;
}

.image-caption {
  margin: 10px 2px 0;
  font-size: 0.74rem;
  color: #8a8a8a;
}

.detail-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-group {
  background: #242424;
  border-radius: 8px;
  padding: 12px 14px;
}

.detail-group-title {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8a8a8a;
  margin-bottom: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 3px 0;
  font-size: 0.8rem;
}

.detail-label {
  color: #9a9a9a;
  flex-shrink: 0;
}

.detail-value {
  text-align: right;
  word-break: break-word;
}

.detail-value.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
}

.detail-value.small {
  font-size: 0.68rem;
  color: #b0b0b0;
}

.detail-warning {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin: 8px 0 0;
  font-size: 0.72rem;
  color: #d8b45f;
}
</style>
